import { Controller } from "@hotwired/stimulus"

const HISTORY_RESTORE_EVENT = "browse-filter-history-restore"

export default class extends Controller {
  connect() {
    this.currentPath = window.location.pathname
    this.browseFilterState = this.element.dataset.browseFilterState
    this.pendingPageVisit = null
    this.pendingFrameVisit = null
    this.handleSubmitStart = this.handleSubmitStart.bind(this)
    this.handleSubmitEnd = this.handleSubmitEnd.bind(this)
    this.handleBeforeVisit = this.handleBeforeVisit.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    this.handlePopState = this.handlePopState.bind(this)
    document.addEventListener("turbo:submit-start", this.handleSubmitStart, true)
    document.addEventListener("turbo:submit-end", this.handleSubmitEnd, true)
    document.addEventListener("turbo:before-visit", this.handleBeforeVisit)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    window.addEventListener("popstate", this.handlePopState)
  }

  disconnect() {
    document.removeEventListener("turbo:submit-start", this.handleSubmitStart, true)
    document.removeEventListener("turbo:submit-end", this.handleSubmitEnd, true)
    document.removeEventListener("turbo:before-visit", this.handleBeforeVisit)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    window.removeEventListener("popstate", this.handlePopState)
  }

  handleBeforeVisit(event) {
    const destination = new URL(event.detail.url, window.location.href)
    const currentLocation = new URL(window.location.href)
    const matchesPendingFrameVisit = this.pendingFrameVisit && this.urlsMatch(destination, this.pendingFrameVisit)

    if (matchesPendingFrameVisit) {
      if (this.pendingPageVisit && !this.urlsMatch(destination, this.pendingPageVisit)) {
        // A completed frame response can still be waiting to hand its history
        // action to Turbo while a newer page visit is in flight. Only suppress
        // that known pending-frame destination; a different destination is a
        // newer user navigation and must remain available.
        this.pendingFrameVisit = null
        event.preventDefault()
        return
      }

      this.pendingFrameVisit = null
      if (this.pendingPageVisit && this.urlsMatch(destination, this.pendingPageVisit)) {
        this.pendingPageVisit = null
      }
      return
    }

    if (destination.href !== currentLocation.href) {
      this.pendingPageVisit = destination.href
    }

    // A frame filter response can finish after a page navigation has started.
    // Disable the frame to cancel that request before its history action can
    // replace the newer destination.
    this.element.disabled = true
  }

  handleBeforeCache() {
    this.element.disabled = false
    this.pendingPageVisit = null
    this.pendingFrameVisit = null
  }

  handleSubmitStart(event) {
    const form = event.target
    if (form?.tagName !== "FORM" || form.getAttribute("data-turbo-frame") !== this.element.id) return

    const action = form.dataset.browseFilterAction
    if (action === "advance" || action === "replace") {
      // Turbo reads this supported action when the frame response is rendered
      // and owns the corresponding history state and restoration bookkeeping.
      form.dataset.turboAction = action
      this.pendingFrameVisit = this.formSubmissionUrl(form)
    }
  }

  handleSubmitEnd(event) {
    const form = event.target
    if (form?.tagName !== "FORM" || form.getAttribute("data-turbo-frame") !== this.element.id) return
    if (event.detail?.success !== true) this.pendingFrameVisit = null
  }

  handlePopState() {
    // This controller remains the sole popstate/history owner. Other
    // behaviors can compose with Turbo's restoration path through this event.
    document.dispatchEvent(new CustomEvent(HISTORY_RESTORE_EVENT, {
      detail: { frameId: this.element.id }
    }))

    const frameId = this.element.id
    const poppedUrl = new URL(window.location.href)

    if (!this.canRestoreFrame(poppedUrl)) return

    window.setTimeout(() => {
      if (!this.element.isConnected || !window.Turbo?.visit || !window.Turbo.navigator?.stop) return

      // Turbo has already consumed popstate and updated its restoration
      // bookkeeping. For an exact same-surface filter state, stop its
      // page-level restore request and ask Turbo to render the popped location
      // through this frame without another history action. Other destinations
      // stay on Turbo's native page restoration path.
      this.pendingFrameVisit = poppedUrl.href
      window.Turbo.navigator.stop()
      window.Turbo.visit(poppedUrl.href, { frame: frameId })
    }, 0)
  }

  formSubmissionUrl(form) {
    const url = new URL(form.action || window.location.href, window.location.href)
    if ((form.method || "get").toLowerCase() === "get") {
      url.search = new URLSearchParams(new FormData(form)).toString()
    }
    return url.href
  }

  urlsMatch(left, right) {
    const leftUrl = new URL(left, window.location.href)
    const rightUrl = new URL(right, window.location.href)
    const normalizedParams = (url) => JSON.stringify(
      Array.from(url.searchParams.entries()).sort(([leftKey, leftValue], [rightKey, rightValue]) =>
        `${leftKey}\u0000${leftValue}`.localeCompare(`${rightKey}\u0000${rightValue}`)
      )
    )

    return leftUrl.origin === rightUrl.origin &&
      leftUrl.pathname === rightUrl.pathname &&
      normalizedParams(leftUrl) === normalizedParams(rightUrl)
  }

  canRestoreFrame(url) {
    if (url.pathname !== this.currentPath) return false

    const scope = url.searchParams.get("scope")
    switch (this.browseFilterState) {
      case "preparations:following":
        return scope === "following"
      case "preparations:all":
      case "my_preparations:mine":
        return scope === null
      case "my_preparations:saved":
        return scope === "saved"
      default:
        return true
    }
  }
}
